import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { message, Form } from 'antd';
import {
  useVerifyOTPMutation,
  useForgotPasswordMutation,
} from '../../store/services/authService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TitleText from '../atoms/TitleText';
import { Information } from 'iconsax-react';
import InfoText from '../atoms/InfoText';
import { AuthWrapper } from './AuthWrapper';
import InputOTP from '../atoms/InputOTP';
import Button from '../atoms/Button';

const verifyOTPSchema = z.object({
  otp: z
    .string({
      required_error: 'This field cannot be empty',
      invalid_type_error: 'This field cannot be empty',
    })
    .min(4, 'OTP must be 4 digits')
    .max(4, 'OTP must be 4 digits')
    .regex(/^\d+$/, 'OTP must contain only numbers'),
});

type VerifyOTPFormData = z.infer<typeof verifyOTPSchema>;

export function VerifyOTP() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [timeLeft, setTimeLeft] = useState(120);
  const [canResend, setCanResend] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyOTPFormData>({
    resolver: zodResolver(verifyOTPSchema),
    mode: 'onChange',
  });

  const [verifyOTP, { isLoading }] = useVerifyOTPMutation();
  const [resendOTP, { isLoading: isResending }] = useForgotPasswordMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) {
      message.error('Email is required');
      navigate('/forgot-password');
      return;
    }

    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft, email, navigate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const onSubmit = async (data: VerifyOTPFormData) => {
    if (!email) {
      message.error('Email is required');
      return;
    }

    try {
      const result = await verifyOTP({
        email,
        code: data.otp,
      }).unwrap();

      message.success('OTP verified successfully!');
      navigate(
        `/setup-password?type=RESET&token=${encodeURIComponent(result.token)}`
      );
    } catch (error: any) {
      message.error(
        error?.data?.errors?.default ||
          Object.values(error?.data?.errors || {}).join(', ')
      );
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      message.error('Email is required');
      return;
    }

    try {
      await resendOTP({ email, otpType: 'FORGET_PASSWORD' }).unwrap();
      message.success('OTP has been resent to your email!');
      setTimeLeft(120);
      setCanResend(false);
    } catch (error: any) {
      message.error(
        error?.data?.errors?.default ||
          Object.values(error?.data?.errors || {}).join(', ')
      );
    }
  };

  if (!email) {
    return null;
  }

  return (
    <AuthWrapper>
      <div className="w-full max-w-xl space-y-8" data-test="verify-otp-page">
        <div className="">
          <TitleText
            text="Enter verification code"
            className="!text-2xl !font-bold mb-3"
          />
          <p className="text-gray-600 text-xs" data-test="verify-otp-description">
            We've sent a 4-digit One-Time Password (OTP) to your{' '}
            <strong className="!font-bold">{email}</strong>. Please check your
            inbox and enter the code below to verify your identity and complete
            the process, Check your mail
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8" data-test="verify-otp-form">
          <Form.Item className="flex justify-center">
            <Controller
              name="otp"
              control={control}
              render={({ field }) => (
                <InputOTP
                  id="otp"
                  length={4}
                  size="large"
                  value={field.value || ''}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value.length === 4) {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  onBlur={field.onBlur}
                  status={errors.otp ? 'error' : undefined}
                  helperText={errors.otp?.message}
                  data-test="verify-otp-input"
                />
              )}
            />
          </Form.Item>

          <Form.Item className="mb-6">
            <Button
              type="submit"
              variant="dark"
              disabled={isLoading || isSubmitting}
              className="w-full font-semibold h-12"
              data-test="verify-otp-submit-btn"
            >
              {isLoading || isSubmitting ? 'Verifying...' : 'Confirm OTP'}
            </Button>
          </Form.Item>

          <div className="flex items-start gap-3" data-test="verify-otp-notice">
            <div className="p-2 rounded-lg bg-blue-100">
              <Information size="16" color="#3C91E6" />
            </div>
            <div className="flex-1 space-y-2">
              <TitleText
                text="important notice"
                size="sm"
                className="!font-bold !text-dark-information"
              />
              <p className="text-xs text-blue-400">
                Note: The OTP is valid for 2 minutes only. If you didn't receive
                the email, please check your spam folder or request a new OTP .
              </p>
            </div>
          </div>

          {timeLeft > 0 && (
            <div className="text-center mt-4" data-test="verify-otp-timer">
              <p className="text-gray-600 text-sm font-medium">
                {formatTime(timeLeft)}
              </p>
            </div>
          )}

          <div className="text-center mt-4">
            <InfoText text="Didn't receive a code?" size="sm" />
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={!canResend || isResending}
              className="text-black-500 hover:text-black-800 text-sm font-semibold underline disabled:opacity-50 disabled:cursor-not-allowed"
              data-test="verify-otp-resend-btn"
            >
              {isResending ? 'Sending...' : 'Send again'}
            </button>
          </div>
        </form>
      </div>
    </AuthWrapper>
  );
}
